import semver from 'semver';

export type ModuleIsFreeScrolling = {
  desktop: boolean;
  device: boolean;
};

export type TThemeButtonsDefaultStyles = {
  label: string | undefined;
  shape: 'pill' | 'rectangle' | 'circle';
  backgroundColor: string;
  backgroundOpacity: number;
  backgroundColorOnHover: string | undefined;
  backgroundOpacityOnHover: number;
  borderWidth: number;
  focusColor: string | undefined;
  disabledColor: string | undefined;
  includeButtonBackground: boolean;
  useLargeButton: boolean;
  borderRadius: number;
  textColor: string;
  textColorOnHover: string | undefined;
  shadow: boolean;
  outline: boolean;
  outlineColor: string | undefined;
  fontFamily: string | undefined;
  fontSize: number;
  bold: boolean;
  uppercase: boolean;
};

export const themeButtonsDefaultStyles: {
  [key: string]: TThemeButtonsDefaultStyles;
} = {
  base: {
    label: undefined,
    shape: 'pill',
    backgroundColor: '#060709',
    backgroundOpacity: 100,
    backgroundColorOnHover: undefined,
    backgroundOpacityOnHover: 80,
    borderWidth: 1,
    focusColor: undefined,
    disabledColor: undefined,
    includeButtonBackground: true,
    useLargeButton: false,
    borderRadius: 8,
    textColor: '#fff',
    textColorOnHover: undefined,
    shadow: true,
    outline: false,
    outlineColor: undefined,
    fontFamily: undefined,
    fontSize: 14,
    bold: false,
    uppercase: false,
  },
  '1.5.0': {
    label: undefined,
    shape: 'rectangle',
    backgroundColor: '#1A1A2B',
    backgroundOpacity: 100,
    backgroundColorOnHover: '#1A1A2B',
    backgroundOpacityOnHover: 80,
    borderWidth: 1,
    focusColor: undefined,
    disabledColor: undefined,
    includeButtonBackground: true,
    useLargeButton: false,
    borderRadius: 8,
    textColor: '#fff',
    textColorOnHover: undefined,
    shadow: true,
    outline: false,
    outlineColor: undefined,
    fontFamily: undefined,
    fontSize: 14,
    bold: false,
    uppercase: false,
  },
};

export const getDefaultButtonStyles = (
  moduleVersionOnCreation: string = ''
) => {
  if (!moduleVersionOnCreation || !semver.valid(moduleVersionOnCreation)) {
    return themeButtonsDefaultStyles['base'];
  }

  if (semver.gte(moduleVersionOnCreation, '1.5.0')) {
    return themeButtonsDefaultStyles['1.5.0'];
  }
  //
  return themeButtonsDefaultStyles['base'];
};

export const getDefaultLinkStyles = versionOnCreation => {
  const isModernVersion =
    semver.valid(versionOnCreation) && semver.gte(versionOnCreation, '1.5.0');

  return {
    links: {
      link: isModernVersion ? '#B2B6CA' : '#0000ff',
      visited: isModernVersion ? '#B2B6CA' : '#800080',
      hover: isModernVersion ? '#1A1A2B' : '#0000ff',
      focus: isModernVersion ? '#D1D5E7' : '#e59700',
      active: isModernVersion ? '#D1D5E7' : '#ff0000',
    },
    linksSecondary: {
      link: '#0000ff',
      visited: '#800080',
      hover: '#0000ff',
      focus: '#e59700',
      active: '#ff0000',
    },
  };
};

export const getDefaultMenuStyles = versionOnCreation => {
  const isModernVersion =
    semver.valid(versionOnCreation) && semver.gte(versionOnCreation, '1.5.0');

  return {
    'Menu background colour': '#ffffff',
    'Menu text and close colour': '#000000',
    'Menu text hover colour': isModernVersion ? '#D1D5E7' : '#0000ff',
    'Menu text active colour': isModernVersion ? '#B2B6CA' : '#ff0000',
  };
};

//
export type TButton = TThemeButtonsDefaultStyles & {
  shape: 'pill' | 'rectangle' | 'circle';
  label: string | undefined;
  backgroundColorOnHover: string | undefined;
  focusColor: string | undefined;
  textColorOnHover: string | undefined;
  outlineColor: string | undefined;
  fontFamily: string | undefined;
};

export type TButtons = {
  [key: string]: TButton;
};

export type Element = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';

export type ElementStyle = {
  fontFamily: string;
  fontSize: number;
  color: string;
  bold?: boolean;
  italic?: boolean;
  lineHeight: number;
};

export type ElementStyles = {
  [key in Element]?: ElementStyle;
};

export type TypeSettings = {
  type: 'basic' | 'advanced';
  fontFamily: string;
  fontFamilies: string[];
  fontSize: number;
  color: string;
  lineHeight: number;
  links: {};
  bold?: boolean;
  italic?: boolean;
  elementStyles: ElementStyles;
};

export type TTheme = {
  backgroundColor: string;
  buttonBackgroundColor: string;
  buttonBorderRadius: number;
  buttonOutline: boolean;
  buttonShadow: boolean;
  buttonShape: string;
  useLargeButton: boolean;
  buttonTextColor: string;
  buttonHover: string;
  buttons: TButtons;
  fontFamilies: any[];
  fontFamily: string;
  fontSize: number;
  lineHeight: number;
  padding: number;
  margin: number;
  paragraphSpacing: number;
  typeSettings?: TypeSettings;
  fontSizes: {
    'Large screen': number;
    'Medium screen': number;
    'Small screen': number;
  };
  highContrast: boolean;
  recoloredChameleonImages: any;
  loadingSpinnerColor: string;
  textColor: string;
  useImagePreloader: boolean;
  enableBookmarking: boolean;
  textToSpeech: boolean;
  textToSpeechVoice: string;
  moduleIsFreeScrolling: ModuleIsFreeScrolling;
  giveFocusToFirstSectionOnLoad: boolean;
  disableLockDownInPreview: boolean;
  userPalette: any[];
  menu: {
    'Menu background colour'?: string;
    'Menu text and close colour'?: string;
    'Menu text hover colour'?: string;
    'Menu text active colour'?: string;
  };
  headings: {
    h1: string | number;
    h2: string | number;
    h3: string | number;
    h4: string | number;
    h5: string | number;
    h6: string | number;
  };
  links: {
    link?: string;
    visited?: string;
    hover?: string;
    focus?: string;
    active?: string;
  };
  linksSecondary?: {
    link?: string;
    visited?: string;
    hover?: string;
    focus?: string;
    active?: string;
  };
};

export const getTheme = (versionOnCreation: string = '') => {
  const isModernVersion =
    semver.valid(versionOnCreation) && semver.gte(versionOnCreation, '1.5.0');

  console.log('LOADING APPROPRIATE THEME FOR :', versionOnCreation || '<1.5.0');

  return {
    backgroundColor: isModernVersion ? '#FBFBFD' : '#ffffff',
    buttonBackgroundColor: isModernVersion ? '#1A1A2B' : '#060709',
    buttonBorderRadius: 8,
    buttonOutline: false,
    buttonShadow: false,
    buttonShape: isModernVersion ? 'rectangle' : 'pill',
    useLargeButton: false,
    buttonTextColor: '#ffffff',
    buttonHover: '',
    fontFamilies: [],
    fontFamily: '',
    fontSize: 16,
    lineHeight: 1.5,
    padding: 10,
    margin: 0,
    paragraphSpacing: 16,
    fontSizes: {
      'Large screen': 16,
      'Medium screen': 16,
      'Small screen': 16,
    },
    loadingSpinnerColor: isModernVersion ? '#E8EDFF' : '#cea5f9',
    buttons: {
      main: getDefaultButtonStyles(versionOnCreation),
    },
    highContrast: false,
    recoloredChameleonImages: {},
    textColor: isModernVersion ? '#1A1A2B' : '#060709',
    useImagePreloader: true,
    enableBookmarking: true,
    moduleIsFreeScrolling: {
      desktop: false,
      device: false,
    },
    giveFocusToFirstSectionOnLoad: true,
    textToSpeech: false,
    textToSpeechVoice: '',
    disableLockDownInPreview: false,
    userPalette: [],

    menu: getDefaultMenuStyles(versionOnCreation),
    //
    headings: {
      // h1: 1,
      // h2: 1,
      // h3: 1,
      // h4: 1,
      // h5: 1,
      // h6: 1,
    } as any,
    //
    //
    // links: {},
    // linksSecondary: {},
    ...getDefaultLinkStyles(versionOnCreation),

    // typeSettings: {} as TypeSettings,
  } as TTheme;
};
